import { Card, Col, Row } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import HelpTooltip from '@/app/common/components/HelpTooltip';
import SubtleBadge from '@/components/common/SubtleBadge';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import { toPersianDigit } from '@/helpers/utils';
import type {
  SeoOverviewData,
  SeoPriorityTone
} from '@/app/ContentTools/types/aiContentAnalysisApiTypes';
import AiLikenessGauge, {
  getScoreGaugeVerdict,
  SEO_SCORE_GAUGE_ZONES
} from './AiLikenessGauge';
import { OverviewCardTitle } from './OverviewSectionTitle';

type SeoScoreCardProps = {
  data: SeoOverviewData;
  className?: string;
  showCriteria?: boolean;
};

const getCriterionTone = (score: number): SeoPriorityTone => {
  if (score >= 9) return 'success';
  if (score >= 7) return 'info';
  if (score >= 5) return 'warning';
  return 'danger';
};

const getCriterionStatus = (score: number) => {
  if (score >= 9) return 'عالی';
  if (score >= 7) return 'خوب';
  if (score >= 5) return 'متوسط';
  return 'ضعیف';
};

const ScoreMetric = ({
  label,
  value,
  icon,
  iconClassName,
  iconSize = '0.95em',
  helpHtml
}: {
  label: string;
  value: string;
  icon: IconProp;
  iconClassName?: string;
  iconSize?: string;
  helpHtml: string;
}) => (
  <div className="d-flex justify-content-between align-items-center bg-100 border rounded-3 px-3 py-2">
    <span className="d-inline-flex align-items-center gap-1 fs--1 text-600 lh-1">
      <FontAwesomeIcon
        icon={icon}
        className={`d-block flex-shrink-0 ${iconClassName || ''}`}
        style={{ width: iconSize, height: iconSize }}
      />
      <span className="lh-1">{label}</span>
      <HelpTooltip placement="top" html={helpHtml} />
    </span>
    <span className="fw-bold fs-0 lh-1">{value}</span>
  </div>
);

const SeoScoreCard = ({
  data,
  className,
  showCriteria = true
}: SeoScoreCardProps) => {
  const percent = (data.overall_score / data.overall_max) * 100;
  const gaugeVerdict = getScoreGaugeVerdict(percent);
  const sortedCriteria = [...data.structure].sort((a, b) => b.score - a.score);

  return (
    <Card className={className}>
      <FalconCardHeader
        title={
          <OverviewCardTitle
            icon="search"
            title="امتیاز سئو"
            helpHtml="این امتیاز وضعیت کلی سئوی صفحه را بر اساس معیارهای محتوایی و فنی نشان می‌دهد و جایگزین بررسی کامل تخصصی نیست."
          />
        }
        titleTag="h6"
        titleClass="d-flex align-items-center"
        className="py-2"
        light
      />
      <Card.Body className="d-flex flex-column flex-grow-1">
        <AiLikenessGauge
          value={percent}
          displayValue={toPersianDigit(data.overall_score.toFixed(1))}
          zones={SEO_SCORE_GAUGE_ZONES}
        />

        <div className="text-center mb-3 ai-likeness-gauge-caption">
          <div className="fw-bold fs-0">{gaugeVerdict}</div>
          <SubtleBadge bg="success" pill className="mt-2">
            از {toPersianDigit(`${data.overall_max}`)}
          </SubtleBadge>
        </div>

        <Row className={`g-2 ${showCriteria ? 'mb-3' : 'mt-auto'}`}>
          <Col xs={6}>
            <ScoreMetric
              label="نقاط قوت"
              icon="trophy"
              iconClassName="text-success"
              value={toPersianDigit(`${data.strengths_count}`)}
              helpHtml="تعداد مواردی که در صفحه به‌خوبی رعایت شده‌اند."
            />
          </Col>
          <Col xs={6}>
            <ScoreMetric
              label="نقاط ضعف"
              icon="bolt"
              iconClassName="text-warning"
              value={toPersianDigit(`${data.weaknesses_count}`)}
              helpHtml="تعداد مواردی که نیاز به اصلاح دارند."
            />
          </Col>
        </Row>

        {showCriteria ? (
          <div className="border-top pt-3">
            <div className="fs--2 text-500 fw-semi-bold mb-2">معیارها</div>
            <div className="d-flex flex-column gap-1">
              {sortedCriteria.map(item => (
                <div
                  key={item.name}
                  className="d-flex align-items-center justify-content-between gap-2 bg-100 border rounded-3 px-2 py-1"
                >
                  <span className="fs--1 text-700">{item.name}</span>
                  <div className="d-flex align-items-center gap-2 flex-shrink-0">
                    <span className="fs--2 fw-semi-bold text-600">
                      {toPersianDigit(`${item.score}`)} / ۱۰
                    </span>
                    <SubtleBadge
                      bg={getCriterionTone(item.score)}
                      className="fs--2"
                    >
                      {getCriterionStatus(item.score)}
                    </SubtleBadge>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </Card.Body>
    </Card>
  );
};

export default SeoScoreCard;
